import { IconButton } from "@circleco/compass/components/IconButton";
import { type PropsWithChildren } from "react";
import { useShallow } from "zustand/react/shallow";

import { t } from "@/i18n-js/instance";

import { useCircleAIShellStore } from "./store/circleAIShellStore";

export interface DismissableBannerProps extends PropsWithChildren {
  id: string;
  onDismiss?: () => void;
  role?: "alert" | "status";
}

export const DismissableBanner = ({
  id,
  children,
  onDismiss,
  role,
}: Readonly<DismissableBannerProps>) => {
  const { dismissBanner, isDismissed } = useCircleAIShellStore(
    useShallow(s => ({
      dismissBanner: s.dismissBanner,
      isDismissed: Boolean(s.dismissedBanners[id]),
    })),
  );

  if (isDismissed) return null;

  const handleDismiss = () => {
    dismissBanner(id);
    onDismiss?.();
  };

  return (
    <div
      data-banner-id={id}
      data-testid={`circle-ai-shell-banner-${id}`}
      role={role}
      aria-live={role === "status" ? "polite" : undefined}
      className="flex shrink-0 items-center bg-black p-4 text-white"
    >
      <div className="flex-1">{children}</div>
      <IconButton
        onClick={handleDismiss}
        icon="cross"
        size="sm"
        variant="outline"
        aria-label={t("circle_ai_shell.close_banner")}
      />
    </div>
  );
};

DismissableBanner.displayName = "CircleAIAppShell.DismissableBanner";
